// Turn-taking for the Live Interview (LIVE_INTERVIEW_PLAN.md §3 and §8, decided 2026-10-30).
//
// The browser measures the microphone's loudness every ~100 ms and feeds it here. This
// decides when the candidate STARTED speaking (which stops the countdown) and when
// they've FINISHED (which ends the answer), without cutting off a non-native speaker
// who pauses to find a word:
//   - the first ~0.6 s after the microphone opens sets the room's noise level;
//   - speech is loudness clearly above that level, held for ~0.25 s;
//   - an answer never ends in its first 10 s;
//   - after that it ends after ~3 s of silence, longer for a speaker who has already
//     paused that long mid-answer and carried on (up to 5 s);
//   - there is no limit on how long the answer is (L12).
// Pure logic with no browser APIs, so it can be tested with made-up loudness patterns.

export const TURN_DEFAULTS = {
  calibrationMs: 600, // noise level measured right after the microphone opens
  minThreshold: 0.015, // RMS loudness that always counts as quiet
  noiseFactor: 2.5, // speech is this many times louder than the room
  speechHoldMs: 250, // loud for this long = speaking (a cough or click isn't)
  minAnswerMs: 10000, // never end an answer before this
  baseSilenceMs: 3000, // the usual pause that ends an answer
  maxSilenceMs: 5000, // the longest it waits, for a speaker who pauses a lot
  resumedPauseMarginMs: 1000, // wait this much longer than their longest resumed pause
  minCountedPauseMs: 700, // shorter gaps are just between words
  // "Could you repeat the question?" (product owner, 2026-10-30): a first utterance this
  // short, followed by this much silence, is checked as a possible repeat request before
  // the 10-second minimum would let the answer end.
  shortUtteranceMs: 4000,
  repeatCheckSilenceMs: 1500,
};

export function createTurnTracker(options = {}) {
  const o = { ...TURN_DEFAULTS, ...options };
  let startedAt = null;
  let calibration = [];
  let threshold = null;
  let loudSince = null;
  let speechStartAt = null;
  let lastSpeechAt = null;
  let longestResumedPause = 0;
  let ended = false;

  const silenceLimit = () =>
    Math.min(o.maxSilenceMs, Math.max(o.baseSilenceMs, longestResumedPause + o.resumedPauseMarginMs));

  return {
    // Feed one loudness reading (RMS, 0-1) at time t (ms). Returns "speech_start",
    // "end_of_answer" or null.
    sample(t, level) {
      if (ended) return null;
      if (startedAt === null) startedAt = t;
      if (threshold === null) {
        if (t - startedAt < o.calibrationMs) {
          calibration.push(level);
          return null;
        }
        const floor = calibration.length ? calibration.reduce((a, b) => a + b, 0) / calibration.length : 0;
        threshold = Math.max(o.minThreshold, floor * o.noiseFactor);
        calibration = [];
      }
      const loud = level > threshold;
      if (speechStartAt === null) {
        if (!loud) {
          loudSince = null;
          return null;
        }
        if (loudSince === null) loudSince = t;
        if (t - loudSince >= o.speechHoldMs) {
          speechStartAt = loudSince;
          lastSpeechAt = t;
          return "speech_start";
        }
        return null;
      }
      if (loud) {
        const pause = t - lastSpeechAt;
        if (pause >= o.minCountedPauseMs) longestResumedPause = Math.max(longestResumedPause, pause);
        lastSpeechAt = t;
        return null;
      }
      if (t - speechStartAt >= o.minAnswerMs && t - lastSpeechAt >= silenceLimit()) {
        ended = true;
        return "end_of_answer";
      }
      return null;
    },
    // Whether this reading would count as speech (for the cut-off check after an end).
    isSpeech(level) {
      return threshold !== null && level > threshold;
    },
    get speaking() {
      return speechStartAt !== null;
    },
    get silenceLimitMs() {
      return silenceLimit();
    },
    get threshold() {
      return threshold;
    },
    get speechStartAt() {
      return speechStartAt;
    },
    // A short first utterance followed by a pause, early in the answer: worth checking
    // whether it was a request to repeat the question (only from the start of speech up
    // to the first time this is true; the caller checks once).
    shortUtterancePaused(t) {
      return (
        speechStartAt !== null &&
        !ended &&
        lastSpeechAt - speechStartAt <= o.shortUtteranceMs &&
        t - lastSpeechAt >= o.repeatCheckSilenceMs &&
        t - speechStartAt < o.minAnswerMs
      );
    },
  };
}

// The microphone's loudness (RMS of the waveform, 0-1) from a Web Audio analyser.
export function rmsFromAnalyser(analyser, buffer) {
  analyser.getFloatTimeDomainData(buffer);
  let sum = 0;
  for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
  return Math.sqrt(sum / buffer.length);
}

// The countdown to START answering (L11): 30 s after a main question, 20 s after a follow-up.
export const START_COUNTDOWN_S = { main: 30, follow_up: 20 };

// A visible hint (not a timer) if silence is never detected, e.g. constant background noise.
export const STILL_LISTENING_HINT_MS = 4 * 60 * 1000;
